import { useMemo } from "react";
import { type SrpProductData } from "../../../../../../../utils/models";

import { useTranslation } from "react-i18next";
import { SRPPackageRestrictionContext } from "~/common/components/PackageRestrictionContext/SRPPackageRestrictionContext";
import { calculateSalesCampaign4CampaignPage, useSelectPricingDetails } from "~/SalesCampaign/utils";
import { SaleTag } from "~/SalesCampaign/components/SaleTag/SaleTag";

import { Price as PriceV3, type PriceProps } from "~/common/components/Price/v3/Price";
import { SalesDataProvider, useSalesData } from "~/SalesCampaign/useSalesData";
import {
  ProductContext,
  ProductDescriptorContext,
  useProductDescriptorContext,
} from "~/feature/Product/ProductContext";
import { Box, Button, Card, CardContent, Divider, Paper, Stack, styled, Typography } from "@mui/material";
import { useResultTileZoovuParams } from "~/Search/utils/searchUtils";
import { ProductService } from "~/AddToCart/ProductService";
import { FontSize, getFontStyle } from "~/common/components/materialInput/Fonts";
import { getInitialPriceReference } from "~/AddToCart/redux/addToCart/getInitialPriceReference";
import { Product } from "~/feature/Product";
import { Parameters, ProductParams2 } from "~/common/components/Product/ProductParamsV2";

import { getFeaturesAsParams } from "./getFeatureData";

import { type UnitMode } from "~/feature/UnitMode/UnitMode";
import { constructAvailability } from "./constructAvailability";

import {
  isProductComparisonCheckboxVisible,
  ProductComparisonCheckbox,
} from "~/ProductComparison/ProductComparisonCheckbox";
import { usePurchasePlace } from "~/feature/Analytics/ga/recommendations/RecommendationContexts";
import { getGALocationForSearchItem, useSearchPageType } from "~/Search/useSearchPageType";
import { URL_RECONDITIONABLE } from "~/utils/url/staticUrls";
import RichTooltip from "~/common/components/RichTooltip/RichTooltip";
import Tag from "~/common/components/Tag/Tag";
import Cycle from "~/common/icons/Cycle";
import parse from "html-react-parser";
import { isNonNull } from "~/utils/reducers";
import { useNumericScreenSize } from "~/common/hooks/useNumericScreenSize";
import { getListPrice } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import { PriceElements } from "~/common/components/Price/PriceV4";
import { Permission, userHasPermission } from "~/utils/permissions";
import StockIndicator from "~/common/components/StockIndicator/StockIndicator";
import { useAddProductToWishlistFlow } from "~/common/hooks/useAddProductToWishlistFlow";
import { AddToCartService } from "~/AddToCart/AddToCartService";
import { renderButtonsNew } from "../useSrpButtons";
import { userIsCustomer } from "~/utils/roles";
import {
  C_INFORMATION_CONTAINER,
  C_ON_INFORMATION_CONTAINER,
  C_ON_SURFACE,
  C_SURFACE,
} from "~/common/components/icon/Colors";
import MMDAssistant from "~/ProductDetails/components/MMDAssistant/MMDAssistant";
import type { ProductData } from "~/models/ProductData";
import { LinkType } from "~/common/components/Link";
import { withDefaults } from "~/common/hoc/withDefaults";
import { useSrpCmnLogic } from "~/ProductDetails/components/MMDAssistant/logic/useSrpCmnLogic";
import classNames from "classnames";
import { useProductHelper } from "~/feature/ProductDescriptor/v2/ProductHelper";
import { SaleQuantityIndicator } from "~/SalesCampaign/components/SaleQuantityIndicator/SaleQuantityIndicator";
import { SaleEndIndicator } from "~/SalesCampaign/components/SaleEndIndicator/SaleEndIndicator";
import { SaleDiscountIndicator } from "~/SalesCampaign/components/SaleDiscountIndicator/SaleDiscountIndicator";
import { useUnitMode } from "~/feature/UnitMode/useUnitMode";
import { Parameter, ParameterStack } from "./Parameter";
import { INVALID_PACKAGE_CODE } from "~/utils/constants";

interface Props {
  // @deprecated - this property is not used since phase in out flag feature was removed
  isPdp?: boolean;
  productCode: string;
  index: number;
  onProductClick?: (area: string, product: SrpProductData) => void;
  searchQuery: string;
  className?: string;
  isTechnicalView?: boolean;
}

export const SearchResultListItemNew = ({
  isTechnicalView,
  productCode,
  onProductClick,
  isPdp,
  index,
  searchQuery,
  className,
}: Props) => {
  const originalProductDescriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference: getInitialPriceReference(),
    options: { fetch: true, fetchAvailability: false },
  });

  const productDescriptor = useMemo(
    () => constructAvailability(originalProductDescriptor),
    [originalProductDescriptor],
  );

  const mmdCmnData = useSrpCmnLogic(productDescriptor.base);

  const stock = productDescriptor.base.stockAmount;
  const inStock = (stock ?? 0) > 0;

  return (
    <ItemRoot
      className={classNames("jsSearchResultProduct", className)}
      data-test-id={`search-result-item`}
      {...useResultTileZoovuParams(productDescriptor.base)}
    >
      {productDescriptor.base.successorProduct && (
        <NewVersionMessage inStock={inStock} successorProduct={productDescriptor.base.successorProduct} />
      )}
      <SearchResultListItemContentNew
        productCode={productCode}
        onProductClick={onProductClick}
        isPdp={isPdp}
        index={index}
        searchQuery={searchQuery}
        isTechnicalView={isTechnicalView}
      />
      {productDescriptor.base.displayMMDAssistant && (
        <MMDMessage
          marketDesignation={productDescriptor.base.marketDesignation}
          initialData={{
            quantity: 1,
            priceReference: productDescriptor.priceReference,
            ...mmdCmnData,
          }}
        />
      )}
    </ItemRoot>
  );
};

const MMDMessage = styled(
  withDefaults(MMDAssistant, {
    variant: "outlined",
    elevation: 0,
    isPDP: true,
  }),
)(({ theme }) => ({
  border: "none",
  borderRadius: 0,
  paddingBlock: theme.spacing(1),
  paddingInline: theme.spacing(2),
}));

export const SearchResultListItemContentNew = ({ productCode, isPdp, className, isTechnicalView }: Props) => {
  const pageSize = useNumericScreenSize();

  const originalProductDescriptor = ProductService.useProductDescriptor({
    productCode,
    priceReference: getInitialPriceReference(),
  });

  const productDescriptor = useMemo(
    () => constructAvailability(originalProductDescriptor),
    [originalProductDescriptor],
  );
  const productHelper = useProductHelper(productDescriptor);

  const pageType = useSearchPageType();
  const gaLocation = getGALocationForSearchItem(isPdp, pageType);

  const unitMode = useUnitMode();

  const canSeePrice = userHasPermission(Permission.PRICE) && !productDescriptor.base.externalProduct;
  const canSeeStock = userHasPermission(Permission.AVAILABILITY) && !productDescriptor.base.externalProduct;
  const showHorizontalDivider = pageSize < useNumericScreenSize.tablet && (canSeePrice || canSeeStock);
  const showVerticalDivider = pageSize >= useNumericScreenSize.tablet;

  const firstSalesCampaignObj = Object.values(productDescriptor.base.salesCampaign ?? {})?.[0]?.[0];
  const salesCampaignCode = firstSalesCampaignObj?.code;
  const { status: salesStatus, data } = useMemo(
    () => calculateSalesCampaign4CampaignPage({ productDescriptor, salesCampaignCode }),
    [productDescriptor, salesCampaignCode],
  );
  const isProductOnSale = productHelper.isProductOnSale();
  const packCode = productHelper.selectBestPackage()?.packCode ?? INVALID_PACKAGE_CODE;

  const salePerPack = data?.salePerPack;
  const salesData = salePerPack?.[packCode];

  const someSaleHasStock = Object.values(data?.salePerPack ?? {}).some((sale) => !sale.soldOut);
  const tagColour = someSaleHasStock ? "active" : "inactive";

  const stock = productDescriptor.base.stockAmount;

  return (
    <SRPPackageRestrictionContext productCode={productCode}>
      <SalesDataProvider value={data}>
        <ProductDescriptorContext value={productDescriptor}>
          <ItemContent>
            {isProductOnSale && <FloatingSaleTag variant={tagColour} />}
            <Box sx={{ justifySelf: "flex-start" }}>
              <Product.PdpLink>
                <Product.PrimaryImageWithLogosV2 size={96} gap={8} />
              </Product.PdpLink>
            </Box>
            <Stack gap={2}>
              <Stack gap={0.5}>
                <Product.DesignationContainer>
                  <Product.PreferredIndicator />
                  <Product.PdpLink>
                    <Product.DesignationV2 variant="titleMedium" fontWeight={"bold"} />
                  </Product.PdpLink>
                </Product.DesignationContainer>
                <Product.MMDChip />
                <Product.ShortDescriptionV2 variant="bodySmall" color="text.secondary" />
              </Stack>
              {!isTechnicalView && <ProductFeatures unitMode={unitMode} />}
              <ProductParameters />
              <ReconditionableTag />
              {isProductComparisonCheckboxVisible(productDescriptor.base) && (
                <ProductComparisonCheckbox productCode={productCode} gaLocation={gaLocation} />
              )}
            </Stack>
            {showHorizontalDivider && <Divider orientation={"horizontal"} flexItem />}
            {showVerticalDivider && <Divider orientation={"vertical"} flexItem />}

            <Stack gap={2}>
              {isProductOnSale && salesData && <StyledSaleEndIndicator endDate={salesData?.endDate} />}
              {canSeePrice && <PriceSection packCode={packCode} />}
              {!isProductOnSale && canSeeStock && <StyledStockIndicator stock={stock ?? 0} />}
              {isProductOnSale && canSeeStock && salesData && (
                <SaleQuantityIndicator.V2 quantity={salesData.salesStock} />
              )}
              <ButtonSection />
            </Stack>
          </ItemContent>
        </ProductDescriptorContext>
      </SalesDataProvider>
    </SRPPackageRestrictionContext>
  );
};

const StyledStockIndicator = styled(StockIndicator.V2)({
  alignSelf: "flex-start",
});

const StyledSaleEndIndicator = styled(SaleEndIndicator.V2)({
  alignSelf: "flex-start",
});

const ReconditionableTag = () => {
  const { t } = useTranslation();
  const descriptor = useProductDescriptorContext();
  if (!descriptor.base.reconditionable) {
    return null;
  }
  return (
    <RichTooltip
      content={<Typography>{parse(t("cmsnavigationnode.schaefflerContentCatalog.reconditionableInfo"))}</Typography>}
      actions={[
        <Button href={URL_RECONDITIONABLE} variant="text">
          {t("cmsnavigationnode.schaefflerContentCatalog.moreInfo")}
        </Button>,
      ]}
    >
      <Tag icon={<Cycle fontSize="small" />} iconPosition="start" variant="success">
        {t("cmsnavigationnode.schaefflerContentCatalog.reconditionable")}
      </Tag>
    </RichTooltip>
  );
};

const ProductParameters = () => {
  const { t } = useTranslation();
  const descriptor = useProductDescriptorContext();
  const context = { t, product: descriptor };

  const data = [
    ProductParams2.SchaefflerId,
    ProductParams2.ProductAliases({ mode: "13-digits", visibleAliasesLayout: "inline" }),
  ]
    .map((param) => param(context))
    .filter(isNonNull);

  return <Parameters data={data} parameterVariant="row" />;
};

const ProductFeatures = ({ unitMode }: { unitMode: UnitMode }) => {
  const descriptor = useProductDescriptorContext();
  const data = getFeaturesAsParams(descriptor.base.features, unitMode);

  if (data.length === 0) {
    return null;
  }

  return (
    <ParameterStack parameterVariant={"row"}>
      {data.map((param, index) => (
        <Parameter
          key={index}
          label={param.label}
          value={param.value ?? "-"}
          slotProps={getSlotParams(param.converted)}
        />
      ))}
    </ParameterStack>
  );
};

function getSlotParams(isConverted: boolean) {
  if (!isConverted) {
    return undefined;
  }
  return {
    label: {
      color: "info",
    },
    value: {
      color: "info",
    },
  };
}

const ItemRoot = styled(Card)(({ theme }) => ({}));

const ItemContent = styled(CardContent)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  position: "relative",

  [theme.breakpoints.up("tablet")]: {
    display: "grid",
    gridTemplateColumns: "136px 1.5fr auto 1fr",
  },
}));

const FloatingSaleTag = styled(SaleTag)({
  position: "absolute",
  top: "0",
  left: "0",
});

export const PriceSection = ({ packCode }: { packCode: string }) => {
  const { t } = useTranslation();
  const data = useSalesData();
  const productDescriptor = useProductDescriptorContext();

  const salesData = data?.salePerPack?.[packCode];

  const { price, oldPrice, netPrice, salesPrice, discountFormatted } = useSelectPricingDetails({
    packCode,
    quantity: 1,
    priceReference: productDescriptor.priceReference,
    productCode: productDescriptor.base.code,
    salesData,
    saleActivated: true,
  });

  const isOnSale = !!salesData;
  const listPrice = getListPrice({ product: productDescriptor, packCode });

  return (
    <Stack gap={2}>
      <Box>
        <Stack direction="row" gap={1} alignItems={"center"}>
          <CurrentPrice price={price} zeroText={t("product.priceOnRequest")} variant="headlineSmall" />
          {isOnSale && <SaleDiscountIndicator.V2 discountFormatted={discountFormatted} variant="titleLarge" />}
        </Stack>
        {isOnSale && <OldPrice price={oldPrice} />}
        <PriceV3.NetPriceUnit price={netPrice} className={"label-medium"} />
        {!isOnSale && <PriceV3.ExtraLabels price={netPrice} />}
      </Box>
      {!isOnSale && listPrice && !!listPrice.value && (
        <Box>
          <PriceElements.Price price={listPrice} variant="titleMedium" fontWeight={"bold"} />
          <PriceElements.ListPriceLabel unit={netPrice?.unit ?? 1} variant="labelMedium" />
        </Box>
      )}
    </Stack>
  );
};

const CurrentPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  fontWeight: soldOut ? "normal" : "bold",
  color: soldOut ? theme.palette.text.secondary : theme.palette.text.primary,
  textDecoration: soldOut ? "line-through" : "none",
}));

const OldPrice = styled(PriceElements.Price, {
  shouldForwardProp: (prop) => prop !== "soldOut",
})<PriceProps & { soldOut?: boolean }>(({ soldOut, theme }) => ({
  ...getFontStyle(FontSize.BodySmall),
  color: soldOut ? theme.palette.text.primary : theme.palette.text.secondary,
  fontWeight: soldOut ? "bold" : "normal",
  textDecoration: soldOut ? "none" : "line-through",
}));

const ButtonSection = () => {
  const productDescriptor = useProductDescriptorContext();
  const priceReference = productDescriptor.priceReference;
  const purchasePlace = usePurchasePlace();

  const pageSize = useNumericScreenSize();
  const useSideButtons = pageSize < useNumericScreenSize.tablet || userIsCustomer();

  const [openAddToWishlist, addToWishlistModal] = useAddProductToWishlistFlow({
    productInput: productDescriptor.base,
    initialQuantity: 1,
    initialPriceReference: productDescriptor.priceReference,
  });

  const onWishlistBtnClick = () => {
    openAddToWishlist();
  };

  const doAddToCart = async () => {
    await ProductService.getProductDescriptor(productDescriptor.base.code, priceReference, { fetch: true }).promise;

    await AddToCartService.openAddToCartWizard(
      {
        productCode: productDescriptor.base.code,
        restrictionQuery: "",
        source: "srp",
      },
      { page: purchasePlace, recommendationPlace: purchasePlace },
    );
  };

  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const addToCartDisabled = webservicesDisabled;

  const buttons = renderButtonsNew(
    productDescriptor,
    {
      atc: {
        onClick: doAddToCart,
        disabled: addToCartDisabled,
      },
      wishlist: {
        onClick: onWishlistBtnClick,
      },
    },
    {
      sideButton: useSideButtons,
      maxButtons: 2,
    },
  );

  return (
    <>
      {addToWishlistModal}
      {useSideButtons && (
        <Stack direction="row" gap={2}>
          {buttons.sideButtons[0]}
          {buttons.buttons[0]}
        </Stack>
      )}
      {!useSideButtons && <Stack gap={1}>{buttons.buttons}</Stack>}
    </>
  );
};

const NewVersionMessage = ({ inStock, successorProduct }: { inStock: boolean; successorProduct: ProductData }) => {
  let color;
  let bgColor;

  if (inStock) {
    color = C_ON_SURFACE;
    bgColor = C_SURFACE;
  } else {
    color = C_ON_INFORMATION_CONTAINER;
    bgColor = C_INFORMATION_CONTAINER;
  }

  return (
    <ProductContext value={successorProduct}>
      <Paper
        sx={{
          px: 2,
          py: 1,
          backgroundColor: bgColor,
          color: color,
          borderRadius: 0,
        }}
      >
        <Typography variant="bodySmall">
          {"This product has a new version: "}
          <Product.PdpLink variant={LinkType.INHERIT}>
            <Product.DesignationV2 variant="bodySmall" fontWeight={"bold"} color="inherit" />
          </Product.PdpLink>
        </Typography>
      </Paper>
    </ProductContext>
  );
};
